/**
 * Nosto-HybridPaginationManager.js
 *
 * Renderiza la paginación que aparece después de completar el límite
 * de productos permitido por una página lógica.
 *
 * No consulta Nosto ni administra infinite scroll. Recibe el estado
 * calculado por el controller y genera enlaces con recarga de página.
 */
(function initializeNostoHybridPaginationManager(global) {
    "use strict";

    class NostoHybridPaginationManager {
        constructor({
            container,
            pageParam = "nostoPage",
            loadedParam = "loaded",
            logicalPageSize = 90,
            labels = {},
            debug = false,
        } = {}) {
            this.container = this.resolveElement(container);
            this.pageParam = this.normalizeString(pageParam) || "nostoPage";
            this.loadedParam = this.normalizeString(loadedParam) || "loaded";
            this.logicalPageSize = this.normalizePositiveInteger(
                logicalPageSize,
                90
            );

            this.labels = {
                previous: "Anterior",
                next: "Siguiente",
                navigation: "Paginación de productos",
                ...labels,
            };

            this.debug = Boolean(debug);
            this.state = null;
        }

        normalizeString(value) {
            return String(value ?? "").trim();
        }

        normalizePositiveInteger(value, fallback) {
            const number = Number(value);

            return Number.isFinite(number) && number > 0
                ? Math.floor(number)
                : fallback;
        }

        resolveElement(target) {
            if (target instanceof Element) return target;

            if (typeof target === "string") {
                return document.querySelector(target);
            }

            return null;
        }

        log(...args) {
            if (!this.debug) return;

            console.info(
                "[NostoHybridPaginationManager]",
                ...args
            );
        }

        buildUrl(page) {
            const url = new URL(global.location.href);
            const normalizedPage = this.normalizePositiveInteger(page, 1);

            url.searchParams.delete(this.loadedParam);

            if (normalizedPage <= 1) {
                url.searchParams.delete(this.pageParam);
            } else {
                url.searchParams.set(
                    this.pageParam,
                    String(normalizedPage)
                );
            }

            return `${url.pathname}${url.search}${url.hash}`;
        }

        createLink({
            label,
            page,
            className = "",
            current = false,
            rel = "",
            ariaLabel = "",
        }) {
            const link = document.createElement("a");

            link.className = [
                "nosto-hybrid-pagination__link",
                className,
            ]
                .filter(Boolean)
                .join(" ");

            link.href = this.buildUrl(page);
            link.textContent = String(label);

            if (current) {
                link.classList.add("is-current");
                link.setAttribute("aria-current", "page");
                link.setAttribute("aria-label", `Página ${page}, actual`);
            } else if (ariaLabel) {
                link.setAttribute("aria-label", ariaLabel);
            }

            if (rel) {
                link.rel = rel;
            }

            return link;
        }

        createEllipsis() {
            const item = document.createElement("span");

            item.className = "nosto-hybrid-pagination__ellipsis";
            item.textContent = "…";
            item.setAttribute("aria-hidden", "true");

            return item;
        }

        getVisiblePages(currentPage, totalPages) {
            if (totalPages <= 5) {
                return Array.from(
                    { length: totalPages },
                    (_, index) => index + 1
                );
            }

            if (currentPage <= 3) {
                return [1, 2, 3, "ellipsis-end", totalPages];
            }

            if (currentPage >= totalPages - 2) {
                return [
                    1,
                    "ellipsis-start",
                    totalPages - 2,
                    totalPages - 1,
                    totalPages,
                ];
            }

            return [
                1,
                "ellipsis-start",
                currentPage - 1,
                currentPage,
                currentPage + 1,
                "ellipsis-end",
                totalPages,
            ];
        }

        hide() {
            if (!this.container) return;

            this.container.hidden = true;
            this.container.replaceChildren();
            this.container.dataset.nostoPaginationVisible = "false";
        }

        render({
            currentPage = 1,
            total = 0,
            localLoaded = 0,
            logicalPageSize = this.logicalPageSize,
            pageComplete = false,
        } = {}) {
            if (!this.container) return null;

            const safePageSize = this.normalizePositiveInteger(
                logicalPageSize,
                this.logicalPageSize
            );

            const safeCurrentPage = this.normalizePositiveInteger(
                currentPage,
                1
            );

            const safeTotal = Math.max(0, Number(total) || 0);
            const totalPages =
                safeTotal > 0
                    ? Math.ceil(safeTotal / safePageSize)
                    : 1;

            this.state = {
                currentPage: safeCurrentPage,
                total: safeTotal,
                totalPages,
                localLoaded: Math.max(0, Number(localLoaded) || 0),
                logicalPageSize: safePageSize,
                pageComplete: Boolean(pageComplete),
            };

            if (!pageComplete || totalPages <= 1) {
                this.hide();
                return this.state;
            }

            const fragment = document.createDocumentFragment();
            const list = document.createElement("div");

            list.className = "nosto-hybrid-pagination__list";

            if (safeCurrentPage > 1) {
                list.appendChild(
                    this.createLink({
                        label: this.labels.previous,
                        page: safeCurrentPage - 1,
                        className: "nosto-hybrid-pagination__previous",
                        rel: "prev",
                        ariaLabel: `Ir a la página ${safeCurrentPage - 1}`,
                    })
                );
            }

            this.getVisiblePages(
                safeCurrentPage,
                totalPages
            ).forEach((page) => {
                if (typeof page === "string") {
                    list.appendChild(this.createEllipsis());
                    return;
                }

                list.appendChild(
                    this.createLink({
                        label: page,
                        page,
                        current: page === safeCurrentPage,
                        ariaLabel: `Ir a la página ${page}`,
                    })
                );
            });

            if (safeCurrentPage < totalPages) {
                list.appendChild(
                    this.createLink({
                        label: this.labels.next,
                        page: safeCurrentPage + 1,
                        className: "nosto-hybrid-pagination__next",
                        rel: "next",
                        ariaLabel: `Ir a la página ${safeCurrentPage + 1}`,
                    })
                );
            }

            fragment.appendChild(list);

            this.container.replaceChildren(fragment);
            this.container.hidden = false;
            this.container.dataset.nostoPaginationVisible = "true";
            this.container.setAttribute(
                "aria-label",
                this.labels.navigation
            );

            this.log("Paginación renderizada.", this.state);

            return this.state;
        }
    }

    global.NostoHybridPaginationManager =
        NostoHybridPaginationManager;
})(window);
